import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AUTH_COOKIE_LOCK_NAME,
  AUTH_ROUTES,
  REFRESH_TIMEOUT_MS,
  TOKEN_STORAGE_KEY,
  USERNAME_STORAGE_KEY,
} from '../authConstants';
import type { AuthAction, AuthState, RenewResult } from '../authTypes';
import type { ResolvedPermissions } from '../../permissions/permissionTypes';
import { ApiError } from '../../../lib/http/ApiError';
import { useNotification } from '../../notifications/hooks/useNotification';
import { AuthContext } from './AuthContext';
import { authApi } from '../authApi';

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        token: action.payload.token,
        username: action.payload.username,
        isAuthenticated: action.payload.token !== null,
        isInitialized: true,
        role: action.payload.role,
        permissions: action.payload.permissions,
      };
    case 'LOGIN':
      return {
        token: action.payload.token,
        username: action.payload.username,
        isAuthenticated: true,
        isInitialized: true,
        role: action.payload.role,
        permissions: action.payload.permissions,
      };
    case 'REFRESH_ME':
      // Same rights → same state object: a 403-triggered resync (useFetch) must
      // not re-render every consumer, nor retrigger effects keyed on `permissions`.
      if (
        action.payload.role === state.role &&
        JSON.stringify(action.payload.permissions) === JSON.stringify(state.permissions)
      ) {
        return state;
      }
      return {
        ...state,
        role: action.payload.role,
        permissions: action.payload.permissions,
      };
    case 'TOKEN_RENEWED':
      // A logout may have won the race against the renewal.
      if (!state.isAuthenticated) return state;
      return { ...state, token: action.payload.token };
    case 'LOGOUT':
      return {
        token: null,
        username: null,
        isAuthenticated: false,
        isInitialized: true,
        role: null,
        permissions: null,
      };
  }
}

const initialState: AuthState = {
  token: null,
  username: null,
  isAuthenticated: false,
  isInitialized: false,
  role: null,
  permissions: null,
};

type MeResult =
  | { kind: 'ok'; role: string | null; permissions: ResolvedPermissions | null }
  // 401 : compte désactivé/supprimé ou jeton expiré — à renouveler, sinon fin de session
  | { kind: 'unauthorized' }
  // réseau / 5xx : on garde la session, droits refusés par défaut
  | { kind: 'unavailable' };

// Résultat interne du renouvellement : `stale` = la demande appartient à une session
// précédente (ou il n'y a plus de session) — refusée SANS effet de bord (HOMCO-1269).
type InternalRenewResult = RenewResult | { kind: 'stale' };

const NO_RIGHTS = { role: null, permissions: null } as const;
const REFUSED = { kind: 'refused' } as const;
const UNAVAILABLE = { kind: 'unavailable' } as const;
const STALE = { kind: 'stale' } as const;

// Rôle et droits absents du login/du localStorage par choix backend — /auth/me est
// l'unique source (hiérarchie déjà appliquée côté serveur). Une session refusée (401)
// est d'abord renouvelée par l'appelant ; un service indisponible ne doit jamais
// bloquer l'auth : on retombe sur `null` (droits refusés par défaut).
function resolveMe(token: string): Promise<MeResult> {
  return authApi
    .me(token)
    .then((me): MeResult => ({ kind: 'ok', role: me.role, permissions: me.resolvedPermissions }))
    .catch(
      (e: unknown): MeResult =>
        ApiError.isApiError(e) && e.isUnauthorized
          ? { kind: 'unauthorized' }
          : { kind: 'unavailable' },
    );
}

// Le cookie de refresh est partagé par tous les onglets : refresh et logout serveur ne
// doivent jamais se chevaucher, sinon le backend purge toutes les sessions (HOMCO-1184).
// Sans Web Locks (jsdom, navigateurs anciens), seule la sérialisation de l'onglet joue.
function withCookieLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return fn();
  // request() resolves to the callback's own promise result; .then() unwraps the typing.
  return locks.request(AUTH_COOKIE_LOCK_NAME, { mode: 'exclusive' }, fn).then(result => result);
}

// Un appel tenu sous le verrou du cookie ne doit jamais le garder indéfiniment : il est
// abandonné après REFRESH_TIMEOUT_MS (HOMCO-1269).
async function withAbortTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    return await run(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useNotification();
  const { t } = useTranslation('common');
  const [state, dispatch] = useReducer(authReducer, initialState);
  const bootstrappedRef = useRef(false);
  const endingRef = useRef(false);
  const refreshingRef = useRef<Promise<void> | null>(null);
  // HOMCO-1269 : source de vérité hors rendu, pour que les fonctions exposées restent
  // stables (useFetch ne doit pas changer d'identité à chaque renouvellement).
  const tokenRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const renewingRef = useRef<Promise<InternalRenewResult> | null>(null);
  const permissionsRef = useRef<ResolvedPermissions | null>(null);
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  const notifySessionEndedRef = useRef(() => {});

  useEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = location;
    permissionsRef.current = state.permissions;
    notifySessionEndedRef.current = () =>
      notify({ message: t('auth.sessionEnded'), severity: 'warning' });
  });

  const clearStoredSession = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USERNAME_STORAGE_KEY);
  }, []);

  // Nouvelle session (login, logout, endSession) : tout ce qui est en vol appartient
  // désormais à une session précédente.
  const startNewSession = useCallback((token: string | null) => {
    generationRef.current += 1;
    tokenRef.current = token;
    renewingRef.current = null;
    refreshingRef.current = null;
  }, []);

  // Fin de session locale, déclenchée par un refus du serveur (401 après échec du
  // renouvellement). Pas d'appel /auth/logout : le jeton est refusé et les refresh tokens
  // ont été purgés. Idempotent : plusieurs requêtes en vol peuvent échouer en même temps.
  // La page courante est mémorisée pour y revenir après reconnexion (HOMCO-1269).
  const endSession = useCallback(() => {
    if (endingRef.current) return;
    endingRef.current = true;
    startNewSession(null);
    clearStoredSession();
    dispatch({ type: 'LOGOUT' });
    notifySessionEndedRef.current();
    const { pathname, search, hash } = locationRef.current;
    navigateRef.current(AUTH_ROUTES.LOGIN, {
      replace: true,
      state: { from: `${pathname}${search}${hash}` },
    });
  }, [clearStoredSession, startNewSession]);

  // Corps du renouvellement, sous le verrou du cookie : un autre onglet a pu renouveler
  // (ou se déconnecter) pendant l'attente du verrou.
  const refreshUnderLock = useCallback(async (failedToken: string): Promise<RenewResult> => {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored === null) return REFUSED;
    // Renouvelé par un autre onglet : adopté sans refresh — il peut déjà avoir expiré.
    if (stored !== failedToken) return { kind: 'renewed', token: stored, adopted: true };

    let accessToken: string;
    try {
      ({ access_token: accessToken } = await withAbortTimeout(signal =>
        authApi.refresh({ signal }),
      ));
    } catch (e: unknown) {
      return ApiError.isApiError(e) && e.isUnauthorized ? REFUSED : UNAVAILABLE;
    }

    // Le stockage est partagé par les onglets : une déconnexion (ici ou ailleurs) pendant
    // le refresh ne doit pas faire revenir un jeton. Le cookie vient d'être tourné, et le
    // jeton tout juste émis est le seul encore valide : on le révoque avec lui, sous le
    // même verrou (HOMCO-1269).
    const current = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (current === null) {
      await withAbortTimeout(signal => authApi.logout(accessToken, { signal })).catch(() => {
        // best effort — the session is over locally either way
      });
      return REFUSED;
    }
    // Une connexion a eu lieu pendant le refresh : on adopte sa session.
    if (current !== failedToken) return { kind: 'renewed', token: current, adopted: true };
    localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
    return { kind: 'renewed', token: accessToken };
  }, []);

  // Renouvellement sans fin de session : utilisé tel quel par le bootstrap et
  // refreshPermissions, enveloppé par renewToken pour useFetch.
  const renewInternal = useCallback(
    (failedToken: string, generation: number): Promise<InternalRenewResult> => {
      if (generation !== generationRef.current) return Promise.resolve(STALE);
      const current = tokenRef.current;
      if (current === null) return Promise.resolve(STALE);
      if (current !== failedToken) {
        // Déjà renouvelé ici, mais le stockage est partagé : vide, l'utilisateur s'est
        // déconnecté dans un autre onglet — ne pas rejouer avec ce jeton (HOMCO-1269).
        if (localStorage.getItem(TOKEN_STORAGE_KEY) === null) return Promise.resolve(REFUSED);
        return Promise.resolve({ kind: 'renewed', token: current, adopted: true });
      }
      if (renewingRef.current) return renewingRef.current;

      const inFlight: Promise<InternalRenewResult> = withCookieLock(() =>
        refreshUnderLock(failedToken),
      )
        .then((result): InternalRenewResult => {
          if (generation !== generationRef.current) return STALE;
          if (result.kind === 'renewed') {
            tokenRef.current = result.token;
            dispatch({ type: 'TOKEN_RENEWED', payload: { token: result.token } });
          }
          return result;
        })
        .finally(() => {
          if (renewingRef.current === inFlight) renewingRef.current = null;
        });
      renewingRef.current = inFlight;
      return inFlight;
    },
    [refreshUnderLock],
  );

  // /auth/me avec renouvellement : un 401 renouvelle le jeton puis relit /auth/me. Un
  // jeton adopté (renouvelé ailleurs) encore refusé a pu expirer entre-temps : un second
  // renouvellement, qui fera cette fois un vrai refresh (HOMCO-1269).
  const resolveMeRenewing = useCallback(
    async (
      token: string,
      generation: number,
    ): Promise<
      { me: MeResult; token: string } | { failed: 'stale' | 'refused' | 'unavailable' }
    > => {
      let me = await resolveMe(token);
      if (me.kind !== 'unauthorized') return { me, token };
      let renewed = await renewInternal(token, generation);
      if (renewed.kind !== 'renewed') return { failed: renewed.kind };
      me = await resolveMe(renewed.token);
      if (me.kind === 'unauthorized' && renewed.adopted) {
        renewed = await renewInternal(renewed.token, generation);
        if (renewed.kind !== 'renewed') return { failed: renewed.kind };
        me = await resolveMe(renewed.token);
      }
      return { me, token: renewed.token };
    },
    [renewInternal],
  );

  // Re-fetches /auth/me (e.g. after the current user's profile or role changed) and
  // updates role + permissions in place. A 401 first renews the token and reads /auth/me
  // once more; only a refused renewal or a second 401 ends the session (HOMCO-1269). An
  // unavailable /auth/me keeps the rights already loaded. Single-flight: N concurrent
  // callers (e.g. N simultaneous 403s in useFetch) share one request. A response that
  // lands after the session changed is ignored — it must never touch the new session.
  const refreshPermissions = useCallback((): Promise<void> => {
    const token = tokenRef.current;
    if (!token) return Promise.resolve();
    if (refreshingRef.current) return refreshingRef.current;

    const generation = generationRef.current;
    const inFlight: Promise<void> = (async () => {
      const result = await resolveMeRenewing(token, generation);
      if ('failed' in result) {
        if (result.failed === 'refused' && generation === generationRef.current) endSession();
        return;
      }
      const { me } = result;
      if (generation !== generationRef.current) return;
      if (me.kind === 'unauthorized') {
        endSession();
        return;
      }
      if (me.kind === 'unavailable') return;
      dispatch({ type: 'REFRESH_ME', payload: { role: me.role, permissions: me.permissions } });
    })().finally(() => {
      if (refreshingRef.current === inFlight) refreshingRef.current = null;
    });
    refreshingRef.current = inFlight;
    return inFlight;
  }, [resolveMeRenewing, endSession]);

  const renewToken = useCallback(
    async (failedToken: string, generation: number): Promise<RenewResult> => {
      const result = await renewInternal(failedToken, generation);
      if (result.kind === 'stale') return REFUSED;
      if (result.kind === 'refused') {
        endSession();
        return result;
      }
      // Droits jamais chargés (bootstrap avec /auth/me indisponible) : on les relit une fois.
      if (result.kind === 'renewed' && permissionsRef.current === null) {
        void refreshPermissions();
      }
      return result;
    },
    [renewInternal, endSession, refreshPermissions],
  );

  // Lecture initiale du localStorage — une seule fois au montage (le garde-fou évite
  // de relancer l'amorçage quand `t` change avec la langue).
  // Le rôle n'est jamais persisté : on le redemande à /auth/me (source unique,
  // toujours à jour côté serveur) plutôt que de risquer une copie locale obsolète.
  // Un jeton expiré est d'abord renouvelé (HOMCO-1269).
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const username = localStorage.getItem(USERNAME_STORAGE_KEY);

    if (!token) {
      dispatch({ type: 'INITIALIZE', payload: { token, username, role: null, permissions: null } });
      return;
    }

    tokenRef.current = token;
    const generation = generationRef.current;
    void (async () => {
      const result = await resolveMeRenewing(token, generation);
      // Renouvellement refusé : comme un 401. Indisponible : session gardée, sans droits.
      const me: MeResult =
        'failed' in result
          ? { kind: result.failed === 'unavailable' ? 'unavailable' : 'unauthorized' }
          : result.me;
      const current = 'failed' in result ? token : result.token;
      // Un login a eu lieu pendant l'amorçage : il a déjà initialisé l'état.
      if (generation !== generationRef.current) return;

      if (me.kind === 'unauthorized') {
        // Session refusée (compte désactivé/supprimé, refresh expiré) : pas d'écran
        // « connecté » sans droits — ProtectedRoute renvoie vers /login.
        tokenRef.current = null;
        clearStoredSession();
        dispatch({
          type: 'INITIALIZE',
          payload: { token: null, username: null, ...NO_RIGHTS },
        });
        notifySessionEndedRef.current();
        return;
      }
      const { role, permissions } = me.kind === 'ok' ? me : NO_RIGHTS;
      dispatch({ type: 'INITIALIZE', payload: { token: current, username, role, permissions } });
    })();
  }, [clearStoredSession, resolveMeRenewing]);

  // La redirection après connexion est faite par LoginPage (page d'origine, HOMCO-1269).
  const login = useCallback(
    async (token: string, username: string) => {
      endingRef.current = false;
      startNewSession(token);
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(USERNAME_STORAGE_KEY, username);
      const me = await resolveMe(token);
      if (me.kind === 'unauthorized') {
        // Compte désactivé entre l'authentification et /auth/me.
        endSession();
        return;
      }
      const { role, permissions } = me.kind === 'ok' ? me : NO_RIGHTS;
      dispatch({ type: 'LOGIN', payload: { token, username, role, permissions } });
    },
    [endSession, startNewSession],
  );

  const logout = useCallback(
    ({ redirect = true }: { redirect?: boolean } = {}) => {
      const token = tokenRef.current;
      // Un refresh en vol voit le stockage vidé et révoque lui-même le cookie avec le jeton
      // qu'il obtient (refreshUnderLock). Le jeton courant vient d'être refusé : un
      // /auth/logout avec lui serait refusé aussi, et chevaucherait le refresh sans Web
      // Locks (HOMCO-1269).
      const refreshInFlight = renewingRef.current !== null;
      startNewSession(null);
      clearStoredSession();
      dispatch({ type: 'LOGOUT' });
      if (redirect) navigateRef.current(AUTH_ROUTES.LOGIN);
      if (token && !refreshInFlight) {
        // Sous le verrou du cookie : ne jamais chevaucher un refresh d'un autre onglet.
        withCookieLock(async () => {
          // Une connexion (cet onglet ou un autre) a eu lieu pendant l'attente : le cookie
          // est désormais celui de la nouvelle session, ne pas le révoquer.
          if (localStorage.getItem(TOKEN_STORAGE_KEY) !== null) return;
          await withAbortTimeout(signal => authApi.logout(token, { signal }));
        }).catch(() => {
          // fire and forget — local logout proceeds regardless of server response
        });
      }
    },
    [clearStoredSession, startNewSession],
  );

  const getToken = useCallback(() => tokenRef.current, []);
  const getSessionGeneration = useCallback(() => generationRef.current, []);

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      endSession,
      refreshPermissions,
      getToken,
      getSessionGeneration,
      renewToken,
    }),
    [
      state,
      login,
      logout,
      endSession,
      refreshPermissions,
      getToken,
      getSessionGeneration,
      renewToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
